import React, {
  Dispatch,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Checkbox } from 'antd';
import { useSelector } from 'react-redux';

import TicketListByPark from '../TicketListByPark';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { OrderContext } from '../..';
import moment from 'moment';
import { parseSecondsToMinutesAndHours } from 'utils/time';
import useCountDown from 'utils/hooks/useCountDown';
import { LoadingOutlined } from '@ant-design/icons';
import { TimeoutModal } from 'app/pages/Checkout/ConfirmPolicyOrder';
import { useHistory } from 'react-router-dom';
import StyledButton from 'app/components/StyledButton';

interface CartDetailProps {
  onPrepareAddingTicketData: () => void;
  isPreparingAddingTicketData: boolean;
  setDisableCreateOrder: Dispatch<React.SetStateAction<boolean>>;
  disableCreateOrder: boolean;
  hasChangeProperty: React.MutableRefObject<boolean>;
  isChangingNumberRef: React.MutableRefObject<boolean>;
}

const TicketList = (props: CartDetailProps) => {
  const {
    onPrepareAddingTicketData,
    isPreparingAddingTicketData,
    disableCreateOrder,
    setDisableCreateOrder,
    hasChangeProperty,
    isChangingNumberRef,
  } = props;

  const {
    orderId,
    ticketsSortedByPark,
    orderDetailData,
    pendingCountdownOrder,
  } = useContext(OrderContext);
  const [isShowTimeout, setIsShowTimeout] = useState(false);
  const translations = useSelector(selectTranslations);
  const history = useHistory();

  useLayoutEffect(() => {
    if (orderDetailData?.orderState?.value === 'cancelled')
      setIsShowTimeout(true);
  }, [orderDetailData]);

  return (
    <LoadingContainer loading={false}>
      <TicketContainer>
        <div className="d-flex justify-content-between align-items-center">
          <StyledH3>
            {translations?.['b2b.txt.order']}: {orderId}
          </StyledH3>
          {orderDetailData?.expiredAt?.datetimeDMYHIS ? (
            <Countdown
              orderCheckout={orderDetailData}
              // onTimeout={() => setIsShowTimeout(true)}
            />
          ) : undefined}
        </div>
        {ticketsSortedByPark.map(ticket => {
          return (
            <div className="ticket-list" key={ticket.park.id}>
              <TicketListByPark
                hasChangeProperty={hasChangeProperty}
                disableCreateOrder={disableCreateOrder}
                setDisableCreateOrder={setDisableCreateOrder}
                title={ticket.park.name}
                tickets={ticket.tickets}
                isChangingNumberRef={isChangingNumberRef}
              />
            </div>
          );
        })}
      </TicketContainer>
      <StyledButton
        textColor="#ffffff"
        style={{ marginTop: 32 }}
        onClick={onPrepareAddingTicketData}
      >
        {isPreparingAddingTicketData
          ? 'Loading...'
          : translations?.['b2b.txt.addTicket'] ?? ''}
      </StyledButton>
      <TimeoutModal
        open={isShowTimeout}
        onClose={async () => {
          await pendingCountdownOrder(orderDetailData?.idHash, '0');
          history.push(
            '/payment-callback?vnp_TxnRef=' + orderDetailData?.idHash,
          );
        }}
      />
    </LoadingContainer>
  );
};

export default TicketList;

const TicketContainer = styled.div`
  border-radius: 20px;
  background: #ffff;
  padding: 40px 30px;
  min-height: 325px;
  box-shadow: rgba(0, 0, 0, 0.05) 0px 20px 20px 0px;
  .ticket-list {
    margin-top: 24px;
    @media (max-width: 1279px) {
      margin-top: 16px;
    }
  }
  @media (max-width: 1279px) {
    padding: 16px;
    min-height: 280px;
  }
`;

const StyledH3 = styled.h3`
  font-weight: 600;
  font-size: 20px;
  text-transform: uppercase;
  margin: 0;
`;

export const StyledCheckbox = styled(Checkbox)`
  flex-direction: row-reverse;
  &.ant-checkbox-wrapper {
    align-items: center;
  }
  .ant-checkbox {
    top: 0;
    &.ant-checkbox-checked {
      .ant-checkbox-inner {
        &::after {
          transform: rotate(45deg) scale(1) translate(-80%, -50%);
        }
      }
    }
  }
  .ant-checkbox-inner {
    height: 16px;
    width: 16px;
    border: solid 2px #c1c1c1;
  }
`;

const Countdown = ({
  onTimeout,
  orderCheckout,
}: {
  onTimeout?: () => void;
  orderCheckout?: Record<string, any>;
}) => {
  const isTimeout = useRef(false);
  const translations = useSelector(selectTranslations);
  const expiredAt = moment(
    orderCheckout?.expiredAt?.datetimeDMYHIS ?? undefined,
    'HH:mm:ss DD/MM/YYYY',
  );
  const startTime = moment(
    orderCheckout?.startTime?.datetimeDMYHIS ?? undefined,
    'HH:mm:ss DD/MM/YYYY',
  );
  let time = expiredAt.valueOf() - startTime.valueOf();
  time = time < 0 ? 0 : time;
  const { timeRemaining } = useCountDown({
    time,
    interval: 1000,
    startTime: startTime.valueOf(),
  });
  const { minutes, seconds, hours } = parseSecondsToMinutesAndHours(
    time / 1000,
  );

  useEffect(() => {
    if (timeRemaining <= 0 && !isTimeout.current) {
      onTimeout && onTimeout();
      isTimeout.current = true;
    }
    return () => {
      isTimeout.current = true;
    };
  }, [timeRemaining, onTimeout]);
  const antIcon = useMemo(
    () => <LoadingOutlined style={{ fontSize: 16, color: '#E92127' }} spin />,
    [],
  );
  return (
    <div>
      {translations ? translations['b2b.txt.payInRemain'] : ''}{' '}
      {timeRemaining === 48 * 60 * 60 * 1000 ? (
        antIcon
      ) : (
        <span style={{ color: '#E92127' }}>{`${hours < 10 ? '0' : ''}${hours}:${
          minutes < 10 ? '0' : ''
        }${minutes}:${seconds < 10 ? '0' : ''}${seconds}`}</span>
      )}
    </div>
  );
};
